import { supabase } from '../lib/supabase';
import {
  cachearCatalogo, mergeCatalogo, leerCatalogo,
  getSyncMeta, setSyncMeta, catalogoActualizado, limpiarCatalogo,
} from './db';

// Subir cuando cambien las columnas cacheadas: fuerza UNA descarga completa para repoblar campos nuevos.
const SCHEMA_VERSION = '2';

// Clave estable por fila para el merge incremental.
const keyStock = (x) => `${x.ubicacion_id}|${x.articulo_id}|${x.lote || ''}|${x.caducidad || ''}`;
const keyId = (x) => x.id;

// Cada tabla: columna real de marca de tiempo (el esquema NO tiene updated_at:
// stock cambia mucho -> incremental por 'actualizado'; el resto por 'creado_at' (altas).
const TABLAS = [
  {
    clave: 'ubicaciones', tabla: 'ubicaciones', tsCol: 'creado_at',
    cols: 'id,codigo,estado,rol,zona_id,almacen_id,d1,d2,d3,lado,capacidad_lpn,motivo_bloqueo,comprobada_en,creado_at',
    colsNoTs: 'id,codigo,estado,rol,zona_id,almacen_id,d1,d2,d3,lado,capacidad_lpn,motivo_bloqueo,comprobada_en',
    filtro: (q) => q.eq('activo', true), limite: 5000, keyOf: keyId,
  },
  {
    clave: 'almacenes', tabla: 'almacenes', tsCol: 'creado_at',
    cols: 'id,codigo,nombre,creado_at', colsNoTs: 'id,codigo,nombre',
    filtro: (q) => q.eq('activo', true), limite: 500, keyOf: keyId,
  },
  {
    clave: 'zonas', tabla: 'zonas', tsCol: 'creado_at',
    cols: 'id,codigo,almacen_id,nombre,perfil,orden,creado_at', colsNoTs: 'id,codigo,almacen_id,nombre,perfil,orden',
    filtro: (q) => q.eq('activo', true), limite: 2000, keyOf: keyId,
  },
  {
    clave: 'articulos', tabla: 'articulos', tsCol: 'creado_at',
    cols: 'id,sku,nombre,creado_at', colsNoTs: 'id,sku,nombre',
    limite: 5000, keyOf: keyId,
  },
  {
    clave: 'stock', tabla: 'stock_ubicacion', tsCol: 'actualizado',
    cols: 'ubicacion_id,articulo_id,lote,caducidad,cantidad,actualizado', colsNoTs: 'ubicacion_id,articulo_id,lote,caducidad,cantidad',
    limite: 20000, keyOf: keyStock,
  },
  {
    clave: 'lpn', tabla: 'lpn', tsCol: 'creado_at',
    cols: 'id,codigo,ubicacion_id,estado,tipo,creado_at', colsNoTs: 'id,codigo,ubicacion_id,estado,tipo',
    limite: 20000, keyOf: keyId,
  },
];

// Supabase limita cada respuesta a 1000 filas: paginamos con range hasta traerlo todo.
async function paginar(construir, limite) {
  const PAGE = 1000;
  let from = 0;
  let all = [];
  for (;;) {
    const { data, error } = await construir().range(from, from + PAGE - 1);
    if (error) return { data: null, error };
    all = all.concat(data || []);
    if (!data || data.length < PAGE || all.length >= limite) break;
    from += PAGE;
  }
  return { data: all, error: null };
}

// Trae TODAS las filas de una consulta paginando con range (evita el tope silencioso de 1000 de PostgREST).
// `construir` debe devolver un query builder de Supabase (con .range disponible). Uso en pantallas
// que necesitan el conjunto completo (Ubicaciones, Mapa) sin límites arbitrarios que oculten datos.
export async function paginarTodo(construir, limite = 100000) {
  return paginar(construir, limite);
}

async function sincronizarTabla(cfg, tid) {
  const since = await getSyncMeta(cfg.clave);
  const base = () => {
    let q = supabase.from(cfg.tabla).select(cfg.cols).eq('tenant_id', tid);
    if (cfg.filtro) q = cfg.filtro(q);
    if (since) q = q.gt(cfg.tsCol, since);
    return q.order(cfg.tsCol, { ascending: true });
  };
  let { data, error } = await paginar(base, cfg.limite);

  if (error) {
    // La columna de marca no existe (u otro fallo): descarga completa como red de seguridad.
    const alt = () => {
      let q = supabase.from(cfg.tabla).select(cfg.colsNoTs).eq('tenant_id', tid);
      if (cfg.filtro) q = cfg.filtro(q);
      return q;
    };
    const r2 = await paginar(alt, cfg.limite);
    if (!r2.error && r2.data) await cachearCatalogo(cfg.clave, r2.data);
    return;
  }
  if (!data) return;

  if (since) await mergeCatalogo(cfg.clave, data, cfg.keyOf);
  else await cachearCatalogo(cfg.clave, data);

  const maxTs = data.reduce((m, x) => (x[cfg.tsCol] && x[cfg.tsCol] > m ? x[cfg.tsCol] : m), since || '');
  if (maxTs) await setSyncMeta(cfg.clave, maxTs);
}

// Cachea/actualiza el catálogo del tenant para escanear y ver fichas SIN red (incremental por updated_at).
// La caché va SIEMPRE ligada al tenant activo (clave 'hueco-catalogo-tid'): si cambia el tenant, se vacía
// antes de repoblar, para no mezclar (ni pintar) datos de otra empresa en usuarios multi-tenant.
const CATALOGO_TID = 'hueco-catalogo-tid';
export function catalogoTidActual() { return localStorage.getItem(CATALOGO_TID); }

let _warmEnCurso = null;
let _ultimoWarm = 0;
export async function warmCatalogo(tid, { forzar = false } = {}) {
  if (!tid || !navigator.onLine) return;
  // Lock anti-concurrencia: Layout (mount+focus), cargarTenants y elegirTenant pueden pedir warm a la vez.
  if (_warmEnCurso) return _warmEnCurso;
  const cambioTenant = catalogoTidActual() && catalogoTidActual() !== tid;
  // Throttle de arranque: sin esto, el arranque encadenaba ~5 warms secuenciales. Un cambio de tenant
  // o el sync manual (forzar) se saltan el throttle para traer YA el catálogo correcto.
  if (!forzar && !cambioTenant && Date.now() - _ultimoWarm < 15000) return;
  _warmEnCurso = (async () => {
    try {
      if (cambioTenant) await limpiarCatalogo(); // vacía la caché de la empresa anterior
      localStorage.setItem(CATALOGO_TID, tid);
      // Si cambió el esquema de columnas cacheadas, resetea el 'since' para repoblar todo una vez.
      if (await getSyncMeta('__schema__') !== SCHEMA_VERSION) {
        await Promise.all(TABLAS.map((cfg) => setSyncMeta(cfg.clave, null)));
        await setSyncMeta('__schema__', SCHEMA_VERSION);
      }
      await Promise.all(TABLAS.map((cfg) => sincronizarTabla(cfg, tid)));
      _ultimoWarm = Date.now();
    } catch (_) { /* sin red: se usa lo ya cacheado */ }
  })();
  try { await _warmEnCurso; } finally { _warmEnCurso = null; }
}

export async function fechaCatalogo() {
  return catalogoActualizado();
}

export async function ubicacionOffline(codigo) {
  const ubis = (await leerCatalogo('ubicaciones')) || [];
  const u = ubis.find((x) => x.codigo === codigo);
  if (!u) return null;
  const stock = ((await leerCatalogo('stock')) || []).filter((s) => s.ubicacion_id === u.id);
  const arts = (await leerCatalogo('articulos')) || [];
  const amap = {};
  arts.forEach((a) => { amap[a.id] = a; });
  return { ubic: u, lineas: stock, arts: amap };
}

export async function lpnOffline(codigo) {
  const lpns = (await leerCatalogo('lpn')) || [];
  return lpns.find((x) => x.codigo === codigo) || null;
}

// Migas de pan SIN red: Almacén › Zona › Ubicación, resueltas desde el catálogo cacheado (IndexedDB).
// `ubicActual`=true → la ubicación es el nivel actual (sin enlace); false → enlaza a su ficha /u/:codigo.
export async function migasOffline(u, { ubicActual = true } = {}) {
  if (!u) return null;
  const [alms, zons] = [
    (await leerCatalogo('almacenes')) || [],
    (await leerCatalogo('zonas')) || [],
  ];
  const alm = alms.find((a) => a.id === u.almacen_id);
  const zon = zons.find((z) => z.id === u.zona_id);
  return [
    { label: alm?.codigo, to: u.almacen_id ? `/mapa/${u.almacen_id}` : null, testid: 'miga-almacen' },
    { label: zon?.codigo, testid: 'miga-zona' },
    ubicActual
      ? { label: u.codigo, actual: true, testid: 'miga-ubicacion' }
      : { label: u.codigo, to: `/u/${u.codigo}`, testid: 'miga-ubicacion' },
  ];
}
